/**
 * Right-sidebar panel describing one extracted Equipment/Instrument (a
 * "subject") — classification, aliases, the documents that reference it
 * (one row per drawing, latest revision), and its linked time-series
 * datastreams. Opened by clicking an alias bounding-box in the viewer.
 * Subject document ids are stable BQ document_ids; entries are resolved from
 * the active site document list and opened by that same BQ identity.
 */
import { plural } from '@lingui/core/macro'
import { useLingui } from '@lingui/react/macro'
import { Link } from '@tanstack/react-router'
import { Button } from 'designSystem/components/Button'
import { useFacilityModelAccess } from 'features/facilityModel/access'
import { subjectResourceKey } from 'features/facilityModel/resourceKeys'
import { useOpenTrendResource } from 'features/facilityModel/useFacilityModelNavigation'
import { prettify } from 'features/fimEvidence'
import { useDocumentTabDataContext } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { useTimeSeriesLinkContext } from 'features/knowledgeDocumentViewer/contexts/TimeSeriesLinkContext'
import { useDocumentSubjectsPreload } from 'features/knowledgeDocumentViewer/hooks/useDocumentSubjects'
import { useSystemDocumentsByBqIds } from 'features/knowledgeDocumentViewer/hooks/useSystemDocumentsByBqIds'
import type { DocumentSubjectsResponse } from 'features/knowledgeDocumentViewer/types/documentSubjects'
import { matchSubjectDatapoints } from 'features/knowledgeDocumentViewer/utils/instrumentTagUtils'
import {
  collectSubjectReferenceTarget,
  type SubjectReferenceTarget,
} from 'features/knowledgeDocumentViewer/utils/subjectReferenceGeometry'
import { SelectedTab } from 'features/navbar/types'
import { useGeckoNotifications } from 'features/notifications'
import type { MatchedDatapoint } from 'features/timeSeries'
import { useOptionalWorkspaceTabsActions } from 'features/workspaceTabs/contexts/WorkspaceTabsContext'
import { Pencil } from 'lucide-react'
import { type FC, useCallback, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import { EntityPanel } from './EntityPanel'
import { Section, TableHeadCell } from './entityPanelStyles'
import { HoverTip } from './HoverTip'
import { latestRevisionDocumentRows } from './relatedDocumentRows'
import {
  type RelatedDocumentRow,
  RelatedDocumentsMessage,
  RelatedDocumentsTable,
} from './RelatedDocumentsTable'
import { SubjectPanelPhotosSection } from './SubjectPanelPhotosSection'
import { TimeSeriesSection } from './TimeSeriesSection'

const AliasTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75rem;
`

const AliasCell = styled.td`
  padding: 0.2rem 0.4rem 0.2rem 0;
  vertical-align: top;
  min-width: 0;
  overflow-wrap: anywhere;
  color: var(--foreground);
`

const KindHeadCell = styled(TableHeadCell)`
  width: 1%;
  padding-right: 0;
  text-align: right;
  white-space: nowrap;
`

const KindCell = styled.td`
  width: 1%;
  padding: 0.2rem 0 0.2rem 0.4rem;
  vertical-align: top;
  text-align: right;
  white-space: nowrap;
  color: var(--muted-foreground);
`

interface SubjectDocumentsPanelProps {
  subjectUid: string
  scrollWithParent?: boolean
  onClose: () => void
}

interface AliasRow {
  value: string
  kind: string
}

export const SubjectDocumentsPanel: FC<SubjectDocumentsPanelProps> = ({
  subjectUid,
  onClose,
  scrollWithParent = false,
}) => {
  const { t } = useLingui()
  const { addDocumentTab } = useKnowledgeDocumentUXContext()
  const { documentSubjects, siteID, documentId } = useDocumentTabDataContext()
  const { canView: canOpenFacilityModel } = useFacilityModelAccess(siteID)
  const workspaceActions = useOptionalWorkspaceTabsActions()
  const { getMatchedDatapoints, getDatapointsByIds } =
    useTimeSeriesLinkContext()
  const openTrend = useOpenTrendResource(siteID, 'document-viewer')
  const { warningNotification } = useGeckoNotifications()
  const preloadDocumentSubjects = useDocumentSubjectsPreload()

  const subject = useMemo(
    () => documentSubjects.find((s) => s.subject_uid === subjectUid),
    [documentSubjects, subjectUid]
  )
  const docIds = useMemo(() => subject?.documents ?? [], [subject])

  const subjectsPreloads = useRef(
    new Map<string, Promise<DocumentSubjectsResponse>>()
  )
  const clickNonce = useRef(0)
  const [openingDocId, setOpeningDocId] = useState<string | null>(null)

  const ensureSubjectsPreload = useCallback(
    (targetDocId: string) => {
      const existing = subjectsPreloads.current.get(targetDocId)
      if (existing) return existing
      const promise = preloadDocumentSubjects(siteID, targetDocId)
      if (!promise) return null
      subjectsPreloads.current.set(targetDocId, promise)
      promise.catch(() => subjectsPreloads.current.delete(targetDocId))
      return promise
    },
    [preloadDocumentSubjects, siteID]
  )

  // Land the tab on the first page referencing the subject, framed on the
  // union of that page's boxes; references on later pages surface as a toast.
  // Without geometry (external users, references persisted without
  // coordinates, or a failed fetch) fall back to opening the document plain.
  const openRelatedDocument = useCallback(
    async (row: RelatedDocumentRow) => {
      const nonce = ++clickNonce.current
      const pending = ensureSubjectsPreload(row.openId)
      let target: SubjectReferenceTarget | null = null
      if (pending) {
        setOpeningDocId(row.openId)
        try {
          const response = await pending
          target = collectSubjectReferenceTarget(
            response.subjects.find((s) => s.subject_uid === subjectUid)
          )
        } catch {
          target = null
        }
        setOpeningDocId((prev) => (prev === row.openId ? null : prev))
        if (nonce !== clickNonce.current) return
      }
      if (!target) {
        addDocumentTab({ documentId: row.openId })
        return
      }
      addDocumentTab({
        documentId: row.openId,
        pageNumber: target.pageNumber,
        regionHighlight: { pageNum: target.pageNumber, boxes: target.boxes },
      })
      if (target.morePages.length > 0) {
        const subjectName = subject?.name ?? t`this subject`
        const pageCount = target.morePages.length
        const pageList = target.morePages.join(', ')
        warningNotification(
          t`More references to ${subjectName} exist on ${plural(pageCount, { one: 'page', other: 'pages' })} ${pageList}`
        )
      }
    },
    [
      t,
      ensureSubjectsPreload,
      subjectUid,
      subject?.name,
      addDocumentTab,
      warningNotification,
    ]
  )

  const aliasRows = useMemo<AliasRow[]>(() => {
    const byKey = new Map<string, AliasRow>()
    for (const alias of subject?.aliases ?? []) {
      const value = alias.value?.trim()
      if (!value) continue
      const key = value.toLowerCase()
      const existing = byKey.get(key)
      if (existing) {
        if (!existing.kind) existing.kind = alias.kind
        continue
      }
      byKey.set(key, { value, kind: alias.kind })
    }
    return [...byKey.values()]
  }, [subject])

  const timeSeriesRows = useMemo<MatchedDatapoint[]>(
    () =>
      matchSubjectDatapoints(subject, getMatchedDatapoints, getDatapointsByIds),
    [subject, getMatchedDatapoints, getDatapointsByIds]
  )

  const { documents: sysDocWrappers, isLoading } =
    useSystemDocumentsByBqIds(docIds)

  const docRows = useMemo(
    () => latestRevisionDocumentRows(docIds, sysDocWrappers ?? [], documentId),
    [sysDocWrappers, docIds, documentId]
  )

  const preloadDocument = useCallback(
    (row: RelatedDocumentRow) => {
      void ensureSubjectsPreload(row.openId)
    },
    [ensureSubjectsPreload]
  )

  const openDocument = useCallback(
    (row: RelatedDocumentRow) => {
      void openRelatedDocument(row)
    },
    [openRelatedDocument]
  )

  const timeSeriesFirst =
    subject?.kind === 'instrument' && timeSeriesRows.length > 0

  const openSubjectInFacilityModel = useCallback(() => {
    if (!subject || !workspaceActions) return
    workspaceActions.openResourceTab({
      resourceKey: subjectResourceKey(subject.subject_uid),
      workspaceDestination: SelectedTab.FacilityModel,
      label: subject.name,
      href: `/facility-model/subjects/${encodeURIComponent(
        subject.subject_uid
      )}?siteId=${encodeURIComponent(siteID ?? '')}`,
    })
  }, [siteID, subject, workspaceActions])

  const timeSeriesSection = timeSeriesRows.length > 0 && (
    <TimeSeriesSection datapoints={timeSeriesRows} onOpenTrend={openTrend} />
  )

  const editAction = subject && canOpenFacilityModel && (
    <HoverTip tip={t`Something wrong? Edit this Subject in the Facility Model`}>
      {workspaceActions ? (
        <Button
          type='button'
          variant='outline'
          size='icon'
          aria-label={t`Edit subject in Facility Model`}
          onClick={openSubjectInFacilityModel}>
          <Pencil />
        </Button>
      ) : (
        <Button asChild variant='outline' size='icon'>
          <Link
            to='/facility-model/subjects/$subjectUid'
            params={{ subjectUid: subject.subject_uid }}
            search={{ siteId: siteID }}
            target='_blank'
            rel='noopener noreferrer'
            aria-label={t`Edit subject in Facility Model`}>
            <Pencil />
          </Link>
        </Button>
      )}
    </HoverTip>
  )

  return (
    <EntityPanel
      scrollWithParent={scrollWithParent}
      eyebrow={t`Subject`}
      title={subject?.name}
      actions={editAction}
      closeLabel={t`Close subject panel`}
      onClose={onClose}>
      {aliasRows.length > 0 && (
        <Section>
          <AliasTable>
            <thead>
              <tr>
                <TableHeadCell>{t`Alias`}</TableHeadCell>
                <KindHeadCell>{t`Kind`}</KindHeadCell>
              </tr>
            </thead>
            <tbody>
              {aliasRows.map((row) => (
                <tr key={row.value}>
                  <AliasCell>{row.value}</AliasCell>
                  <KindCell>{prettify(row.kind) || '—'}</KindCell>
                </tr>
              ))}
            </tbody>
          </AliasTable>
        </Section>
      )}

      {timeSeriesFirst && timeSeriesSection}

      {isLoading ? (
        <RelatedDocumentsMessage message={t`Loading documents…`} />
      ) : docRows.length === 0 ? (
        <RelatedDocumentsMessage
          message={t`No documents reference this subject.`}
        />
      ) : (
        <RelatedDocumentsTable
          rows={docRows}
          openingDocId={openingDocId}
          onPreload={preloadDocument}
          onOpen={openDocument}
        />
      )}

      {!timeSeriesFirst && timeSeriesSection}

      <Section>
        <SubjectPanelPhotosSection siteId={siteID} subjectUid={subjectUid} />
      </Section>
    </EntityPanel>
  )
}
